import React from 'react';
import { Button } from '@appkit4/react-components/button';
import './GenerationErrorOverlay.scss';

interface GenerationErrorOverlayProps {
    message: string;
    error: any;
    prompt: string;
    onRetry: () => void;
    onDismiss: () => void;
}

const GenerationErrorOverlay: React.FC<GenerationErrorOverlayProps> = ({
    message,
    error,
    prompt,
    onRetry,
    onDismiss,
}) => {
    const getErrorDetails = () => {
        if (error?.response?.status) {
            return `Server returned ${error.response.status} error`;
        }
        if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
            return 'Network connection lost';
        }
        if (error?.message?.includes('timeout')) {
            return 'Request timed out';
        }
        return 'Connection failed';
    };

    const getErrorTitle = () => {
        if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
            return 'Connection Lost';
        }
        if (error?.response?.status >= 500) {
            return 'Server Error';
        }
        if (error?.response?.status === 429) {
            return 'Too Many Requests';
        }
        return 'Generation Failed';
    };

    const getSuggestions = () => {
        if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
            return ['Check your internet connection', 'Try again in a moment', 'Contact support if the issue persists'];
        }
        if (error?.response?.status >= 500) {
            return [
                'This is a temporary server issue',
                'Try again in a few moments',
                'The issue has been logged for review',
            ];
        }
        if (error?.response?.status === 429) {
            return [
                'Too many requests in a short time',
                'Wait a moment before retrying',
                'Consider batching your requests',
            ];
        }
        return ['Check your network connection', 'Try simplifying your prompt', 'Contact support if this continues'];
    };

    return (
        <div className="generation-error-overlay">
            <div className="error-content">
                <div className="error-icon-wrapper">
                    <span className="Appkit4-icon icon-alert-triangle-outline error-icon" />
                </div>

                <h2 className="error-title">{getErrorTitle()}</h2>

                <div className="error-details">
                    <p className="error-description">{message}</p>
                    <div className="error-technical">
                        <span className="error-label">Technical details:</span>
                        <span className="error-value">{getErrorDetails()}</span>
                    </div>
                </div>

                {prompt && (
                    <div className="prompt-context">
                        <div className="prompt-label">
                            <span className="Appkit4-icon icon-comment-outline" />
                            Your prompt:
                        </div>
                        <div className="prompt-text">{prompt}</div>
                    </div>
                )}

                <div className="error-suggestions">
                    <h3>What you can do:</h3>
                    <ul>
                        {getSuggestions().map((suggestion, index) => (
                            <li key={index}>
                                <span className="Appkit4-icon icon-checkmark-circle-outline" />
                                {suggestion}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="error-actions">
                    <Button kind="primary" onClick={onRetry} className="retry-button">
                        <span className="Appkit4-icon icon-refresh-outline" />
                        Retry Generation
                    </Button>
                    <Button kind="secondary" onClick={onDismiss}>
                        Dismiss
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default GenerationErrorOverlay;
